import { useLocation, useNavigate } from "react-router";
import { useForm } from "@mantine/form";
import { NumberInput, Flex, Divider, Textarea, Checkbox } from "@mantine/core";
import { TimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import styled from "styled-components";
import { createWorkShift } from "../../api/shiftAssignmentApi";
import { createNotification } from "../../utils/useNotification";
import { theme } from "../../theme";
import ClockIcon from "../common/Icons/ClockIcon";
import { useEffect, useRef } from "react";
import Container from "../common/Container";
import PageHeader from "../common/PageHeader";

const days = [
  { value: "1", label: "Pazartesi" },
  { value: "2", label: "Salı" },
  { value: "3", label: "Çarşamba" },
  { value: "4", label: "Perşembe" },
  { value: "5", label: "Cuma" },
  { value: "6", label: "Cumartesi" },
  { value: "0", label: "Pazar" },
];

const ShiftNewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedDay, selectedRole, selectedDepartment, date } = location.state || {};
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm({
    initialValues: {
      startTime: "",
      endTime: "",
      unpaidBreakMinutes: 0,
      employeeCount: 1,
      workDuration: 0,
      notes: "",
      daysOfWeek: [] as string[],
    },
    validate: {
      startTime: (value: string) => (!value ? "Başlangıç saati gerekli" : null),
      endTime: (value: string) => (!value ? "Bitiş saati gerekli" : null),
      unpaidBreakMinutes: (value: number) => (value < 0 ? "Negatif olamaz" : null),
      employeeCount: (value: number) => (value <= 0 ? "Minimum 1 olmalı" : null),
      workDuration: (value: number) => (value < 0 ? "Negatif olamaz" : null),
    },
  });

  useEffect(() => {
    const { startTime, workDuration } = form.values;
    if (startTime && workDuration > 0) {
      const calculatedEnd = dayjs("2023-01-01T" + startTime)
        .add(workDuration * 60, "minute")
        .format("HH:mm");
      form.setFieldValue("endTime", calculatedEnd);
    }
  }, [form.values.startTime, form.values.workDuration]);

  const handleSubmit = async (values: typeof form.values) => {
    const d = new Date(selectedDay?.start);
    const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const startDateTime = dayjs(`${dateString}T${values.startTime}`);
    const endDateTime = values.workDuration
      ? startDateTime.add(values.workDuration * 60, "minute").toDate()
      : dayjs(`${dateString}T${values.endTime}`).toDate();

    const shift = {
      startTime: startDateTime.format("YYYY-MM-DDTHH:mm:ss"),
      endTime: dayjs(endDateTime).format("YYYY-MM-DDTHH:mm:ss"),
      unpaidBreakMinutes: values.unpaidBreakMinutes,
      count: values.employeeCount,
      notes: values.notes,
      daysOfWeek: values.daysOfWeek.map((d: string) => parseInt(d, 10)),
    };

    try {
      const response: any = await createWorkShift({
        departmentId: selectedDepartment,
        roleId: selectedRole?.value,
        date,
        shift,
      });
      createNotification({ success: response?.success, message: response?.message });
      if (response?.success) navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle={dayjs(selectedDay?.start).format("dddd, MMMM DD")}
        subText={selectedRole?.label ?? ""}
        isButtoned
        buttonText="Kaydet"
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <form ref={formRef} onSubmit={form.onSubmit(handleSubmit)}>
        <FieldSection>
          <SectionLabel>Mesai Saatleri</SectionLabel>
          <Flex gap={16} mb={20}>
            <TimePicker
              w="100%"
              label="Başlangıç Saati"
              {...form.getInputProps("startTime")}
              rightSection={<ClockIcon size={20} />}
            />
            <TimePicker
              w="100%"
              label="Bitiş Saati"
              {...form.getInputProps("endTime")}
              readOnly
              rightSection={<ClockIcon size={20} />}
            />
          </Flex>
          <FieldGrid>
            <NumberInput
              hideControls
              label="Çalışma Süresi (saat)"
              placeholder="Ör: 8"
              step={0.5}
              min={0}
              {...form.getInputProps("workDuration")}
            />
            <NumberInput
              hideControls
              label="Mola Süresi (dakika)"
              {...form.getInputProps("unpaidBreakMinutes")}
            />
            <NumberInput
              hideControls
              label="Çalışan Sayısı"
              {...form.getInputProps("employeeCount")}
            />
          </FieldGrid>
          <Textarea
            label="Notlar"
            placeholder="Vardiya için not girebilirsiniz."
            mt="xl"
            autosize
            minRows={3}
            maxRows={5}
            {...form.getInputProps("notes")}
          />
        </FieldSection>

        <Divider my={24} />

        <FieldSection>
          <SectionLabel>Tekrar</SectionLabel>
          <RepeatDescription>
            Seçili günlerde bu vardiya ay sonuna kadar tekrar edilecek.
          </RepeatDescription>
          <Checkbox.Group {...form.getInputProps("daysOfWeek")}>
            <DayGrid>
              {days.map((day) => (
                <Checkbox
                  key={day.value}
                  color={theme.colors.brand.normal}
                  value={day.value}
                  label={day.label}
                />
              ))}
            </DayGrid>
          </Checkbox.Group>
        </FieldSection>
      </form>
    </Container>
  );
};

export default ShiftNewPage;

const FieldSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RepeatDescription = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0 0 16px;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;